import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../ui/sheet";
import { CheckCircle2, FileDown, Flame, Mail, Pause } from "lucide-react";
import { EVENT_FILTERS, backend, fmt } from "./utils";

export function TimelineDrawer({
  drawerOwner,
  drawerInvoices,
  drawerHistory,
  drawerBusy,
  eventFilter,
  setEventFilter,
  onClose,
  onOpenSendStmt,
  onOpenReply,
}) {
  return (
    <Sheet open={!!drawerOwner} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="timeline-drawer">
        <SheetHeader>
          <SheetTitle>{drawerOwner?.user_name}</SheetTitle>
          <SheetDescription>
            {drawerOwner?.user_email || "—"}
            {drawerOwner?.on_plan && (
              <span className="ml-2 inline-flex items-center gap-1 rounded px-2 py-0.5 bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-200 text-[10px] uppercase tracking-widest font-semibold">
                <Pause size={10} /> On plan · {drawerOwner.payment_plan_until}
              </span>
            )}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-4">
          {drawerOwner?.on_plan && drawerOwner?.payment_plan_note && (
            <div className="rounded-md border border-sky-300/60 bg-sky-50 dark:bg-sky-950/30 p-3 text-xs text-sky-900 dark:text-sky-200" data-testid="drawer-plan-note">
              <span className="font-semibold uppercase tracking-widest text-[10px]">Plan note</span>
              <div className="mt-1 italic">{drawerOwner.payment_plan_note}</div>
            </div>
          )}
          {drawerOwner && (
            <div className="flex flex-wrap gap-2 items-center justify-between">
              <div className="flex gap-1 flex-wrap" data-testid="event-filter-chips">
                {EVENT_FILTERS.map((f) => (
                  <button key={f.key} type="button"
                          onClick={() => setEventFilter(f.key)}
                          className={`text-[10px] uppercase tracking-widest rounded-full px-3 py-1 font-semibold border transition-colors ${
                            eventFilter === f.key
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-transparent text-muted-foreground border-border hover:border-primary/50"
                          }`}
                          data-testid={`event-filter-${f.key}`}>
                    {f.label}
                  </button>
                ))}
              </div>
              <a href={`${backend}/api/owners/${drawerOwner.user_id}/overdue-statement`}
                 target="_blank" rel="noreferrer"
                 className="text-xs inline-flex items-center gap-1 text-primary hover:underline"
                 data-testid="drawer-statement-btn">
                <FileDown size={12} /> Download statement (PDF)
              </a>
              <button type="button" onClick={onOpenSendStmt}
                      disabled={drawerBusy || !drawerOwner?.user_email}
                      className="text-xs inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="drawer-send-statement-btn">
                <Mail size={12} /> Send statement to owner
              </button>
              <button type="button" onClick={onOpenReply}
                      disabled={drawerBusy}
                      className="text-xs inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-50"
                      data-testid="drawer-log-reply-btn">
                <CheckCircle2 size={12} /> Log owner reply
              </button>
            </div>
          )}
          {drawerBusy && <div className="text-xs text-muted-foreground">Loading invoices…</div>}
          {!drawerBusy && drawerHistory.length > 0 && (
            <details className="border border-border rounded-lg" data-testid="drawer-statement-history">
              <summary className="p-3 cursor-pointer text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground flex items-center gap-2 hover:bg-muted/40">
                <FileDown size={11} /> Statement history · {drawerHistory.length} sent
              </summary>
              <ol className="divide-y divide-border">
                {drawerHistory.map((h, i) => (
                  <li key={h.at || `stmt-${i}`} className="p-3 text-xs" data-testid={`stmt-history-${i}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-medium">
                        {new Date(h.at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                      <div className="text-primary font-medium">{h.count} invoice(s) · {fmt(h.total)}</div>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      by <span className="opacity-90">{h.by_name || h.by?.slice(0, 8) || "admin"}</span>
                      {" · "}to <span className="opacity-90">{h.to}</span>
                    </div>
                    {h.note && (
                      <div className="mt-2 rounded bg-muted/60 p-2 italic text-[11px] text-muted-foreground" data-testid={`stmt-history-note-${i}`}>“{h.note}”</div>
                    )}
                  </li>
                ))}
              </ol>
            </details>
          )}
          {!drawerBusy && drawerInvoices.length === 0 && (
            <div className="text-xs text-muted-foreground italic">No invoices for this owner.</div>
          )}
          {drawerInvoices.map((inv) => {
            const overdue = inv.status === "due" && inv.period_end < new Date().toISOString().slice(0, 10);
            const allEvents = inv.reminders || [];
            const events = eventFilter === "all" ? allEvents : allEvents.filter((ev) => ev.kind === eventFilter);
            const paidAmount = Number(inv.paid_amount || 0);
            const invAmount = Number(inv.amount || 0);
            const partialPct = invAmount > 0 && paidAmount > 0 && paidAmount < invAmount
              ? Math.min(100, Math.round((paidAmount / invAmount) * 100))
              : 0;
            return (
              <div key={inv.id} className="border border-border rounded-lg overflow-hidden" data-testid={`drawer-invoice-${inv.id}`}>
                <div className="p-3 bg-muted/40 flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-sm font-medium">{inv.period_start} → {inv.period_end}</div>
                    <div className="text-[11px] text-muted-foreground uppercase tracking-widest">{inv.kind?.replace("_", " ")} · {fmt(inv.amount)}</div>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest rounded px-2 py-0.5 font-semibold ${
                      inv.status === "paid" ? "bg-primary/10 text-primary"
                      : overdue ? "bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-200"
                      : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                    }`}>{inv.status}</span>
                    {inv.escalated_at && (
                      <span className="text-[10px] uppercase tracking-widest rounded px-2 py-0.5 font-semibold bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200 inline-flex items-center gap-1">
                        <Flame size={10} /> escalated
                      </span>
                    )}
                  </div>
                </div>
                {partialPct > 0 && (
                  <div className="px-3 pt-2" data-testid={`partial-bar-${inv.id}`}>
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      <span>Partially paid</span>
                      <span className="font-semibold text-primary">{partialPct}% · {fmt(paidAmount)} / {fmt(invAmount)}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-primary transition-all" style={{ width: `${partialPct}%` }} />
                    </div>
                  </div>
                )}
                <div className="p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">
                    Reminder timeline{eventFilter !== "all" && allEvents.length !== events.length && (
                      <span className="normal-case tracking-normal font-normal opacity-70"> · showing {events.length}/{allEvents.length}</span>
                    )}
                  </div>
                  {events.length === 0 && (
                    <div className="text-xs italic text-muted-foreground">
                      {eventFilter === "all"
                        ? (inv.escalated_at ? "Escalation event flagged." : "No reminders sent yet.")
                        : "No events for this filter."}
                    </div>
                  )}
                  <ol className="relative border-l border-border ml-2 space-y-3">
                    {events.map((ev, i) => (
                      <li key={`${inv.id}-${ev.at || i}`} className="pl-4 relative" data-testid={`drawer-event-${inv.id}-${i}`}>
                        <span className={`absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full ${
                          ev.kind === "escalation" ? "bg-red-500"
                          : ev.kind === "cron_reminder" ? "bg-amber-500"
                          : "bg-primary"
                        }`} />
                        <div className="text-xs font-medium capitalize">{ev.kind?.replace("_", " ")}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {new Date(ev.at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          {ev.to && <> · to <span className="opacity-80">{ev.to}</span></>}
                          {ev.by && <> · by <span className="opacity-80">{ev.by === "cron" ? "system cron" : ev.by.slice(0, 8)}</span></>}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
